// Single source of truth for short-form expansions used across the
// Console UI. Used by the <AutoAbbr> component to decorate any text
// blob with native HTML <abbr title="…"> tooltips, and by chip
// renderers (stream codes, V2E theme keys, team labels) to surface the
// full form on hover.
//
// Ordering matters: longer keys must appear before shorter ones that
// they would otherwise shadow (e.g. "RWE" before "RW" — but "RW" isn't
// a key here; "HRQoL" before "QoL"). The AutoAbbr regex is built in
// descending-length order so this is enforced automatically.

const ABBREVIATIONS = {
  // V2E + PentaVoice streams
  "V2E": "Voice2Evidence",
  "L1": "L1 · Digital & Social Listening (PentaVoice stream)",
  "L2": "L2 · PROs & Surveys (PentaVoice stream)",
  "L3": "L3 · Patient & Advocacy Voice (PentaVoice stream)",
  "L4": "L4 · Real-World Data / Real-World Evidence (PentaVoice stream)",
  "L5": "L5 · Regulatory & HTA (PentaVoice stream)",

  // Indication + disease subtypes (this engagement is SLE)
  "SLE": "Systemic Lupus Erythematosus",
  "CLE": "Cutaneous Lupus Erythematosus",
  "DLE": "Discoid Lupus Erythematosus",
  "SCLE": "Subacute Cutaneous Lupus Erythematosus",
  "LN": "Lupus Nephritis",

  // Functional teams (used in owner chips + prose)
  "MA": "Medical Affairs",
  "HEOR": "Health Economics & Outcomes Research",
  "HTA": "Health Technology Assessment",

  // Patient-reported outcomes / instruments
  "PRO": "Patient-Reported Outcome",
  "PROs": "Patient-Reported Outcomes",
  "PFDD": "Patient-Focused Drug Development (FDA programme)",
  "PRO-LR": "Patient-Reported Outcome — Lupus Research",
  "HRQoL": "Health-Related Quality of Life",
  "QoL": "Quality of Life",
  "QALY": "Quality-Adjusted Life Year",
  "MCID": "Minimal Clinically Important Difference",
  "LupusQoL": "Lupus-specific Quality of Life instrument",
  "SLEQOL": "Systemic Lupus Erythematosus Quality of Life questionnaire",
  "SLEDAI": "Systemic Lupus Erythematosus Disease Activity Index",
  "BILAG": "British Isles Lupus Assessment Group",
  "BICLA": "BILAG-based Composite Lupus Assessment",
  "SLICC": "Systemic Lupus International Collaborating Clinics",
  "SRI": "SLE Responder Index",
  "SF-36": "Short Form 36-item Health Survey",
  "MCS": "Mental Component Summary (SF-36)",
  "PCS": "Physical Component Summary (SF-36)",
  "FACIT-F": "Functional Assessment of Chronic Illness Therapy — Fatigue",
  "WPAI": "Work Productivity and Activity Impairment questionnaire",
  "EQ-5D": "EuroQol 5-Dimension health utility instrument",

  // RWD / RWE / data sources
  "RWD": "Real-World Data",
  "RWE": "Real-World Evidence",
  "MarketScan": "IBM/Merative MarketScan US claims database",
  "CPRD": "Clinical Practice Research Datalink (UK primary-care RWD)",
  "Optum": "Optum US claims database",
  "Truven": "Truven Health Analytics US claims database",
  "IQVIA": "IQVIA real-world data platform",
  "LUNA": "Lupus Registry of Nationwide Institutions (Japan)",
  "AURA": "Asia-Pacific Lupus Collaboration registry",

  // HCPs and stakeholders
  "HCP": "Healthcare Professional",
  "HCPs": "Healthcare Professionals",
  "KOL": "Key Opinion Leader",
  "KOLs": "Key Opinion Leaders",
  "PAB": "Patient Advisory Board",
  "MDT": "Multidisciplinary Team",

  // Patient advocacy bodies
  "LFA": "Lupus Foundation of America",
  "LRA": "Lupus Research Alliance",
  "WLF": "World Lupus Federation",
  "ACR": "American College of Rheumatology",
  "EULAR": "European Alliance of Associations for Rheumatology",
  "ISPOR": "Professional Society for Health Economics and Outcomes Research",

  // Regulators + HTA bodies
  "FDA": "U.S. Food and Drug Administration",
  "EMA": "European Medicines Agency",
  "MHRA": "UK Medicines & Healthcare products Regulatory Agency",
  "PMDA": "Pharmaceuticals and Medical Devices Agency (Japan)",
  "NICE": "National Institute for Health and Care Excellence (UK HTA)",
  "G-BA": "Gemeinsamer Bundesausschuss (Germany Federal Joint Committee)",
  "IQWiG": "Institute for Quality and Efficiency in Healthcare (Germany)",
  "HAS": "Haute Autorité de Santé (France HTA body)",
  "CADTH": "Canada's Drug and Health Technology Agency",
  "PBAC": "Pharmaceutical Benefits Advisory Committee (Australia)",
  "ICER": "Institute for Clinical and Economic Review (US)",
  "SBA": "Summary Basis of Approval (FDA)",
  "EPAR": "European Public Assessment Report (EMA)",

  // Cenerimod-specific mechanism + endpoints
  "S1P1": "Sphingosine-1-Phosphate Receptor 1",
  "MACE": "Major Adverse Cardiovascular Events",
  "OCS": "Oral Corticosteroid",
  "GC": "Glucocorticoid",
  "IRAE": "Immune-Related Adverse Event",
  "AE": "Adverse Event",
  "ADA": "Anti-Drug Antibody",
  "RCT": "Randomised Controlled Trial",
  "RR": "Response Rate",
  "OR": "Odds Ratio",
  "HR": "Hazard Ratio",
  "CI": "Confidence Interval",
  "NRI": "Non-Responder Imputation",
  "ITT": "Intention-To-Treat",
  "SOC": "Standard of Care",
};

// Build a regex that matches any abbreviation as a whole token. Longer
// keys come first so e.g. "HRQoL" matches before "QoL". Boundaries use
// (?<![\w-]) and (?![\w-]) so chained tokens like "L1/L2/L3" or "RWD/RWE"
// each get decorated separately, but words inside other words (e.g. "v2e"
// inside "level2e") are left alone.
const ESCAPED_KEYS = Object.keys(ABBREVIATIONS)
  .sort((a, b) => b.length - a.length)
  .map((k) => k.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"));
const ABBR_REGEX = new RegExp(`(?<![\\w-])(${ESCAPED_KEYS.join("|")})(?![\\w-])`, "g");

export { ABBREVIATIONS, ABBR_REGEX };
